// src/components/Room/MultiplacerCeilingDevicesToolBar.tsx
import React from "react";
import { Box, Button, Group, Text, Tooltip } from "@mantine/core";
import { FaCopy, FaList } from "react-icons/fa6";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectHttpDevicesInRoom } from "../../DesignTab/devices/deviceSlice";
import { selectMlDevicesAvailableInSelectedSpace } from "../../Room/roomSelectors";
import { selectHttpSingleRoomFull } from "../../Room/roomSelectors";
import { FaBrain } from "react-icons/fa";
import { setCopySpaceModeActiveInRedux } from "../../DesignTab/designSlice";
import {
  addDeviceTypeToSelectedRoomThunk,
  copyDevicesFromStandardRoomToSelectedRoomThunk,
  thunkAddMlDevicesToSelectedRoom,
} from "../../DesignTab/devices/deviceThunks";
import { DeleteAllDevicesInRoomButton } from "./DeleteAllDevicesInRoomButton";
import { RoomLockToggleButton } from "./RoomLockToggleButton";
import DeviceTypePicker from "./DeviceTypePicker";

export const MultiplacerCeilingDevicesToolBar: React.FC = () => {
  const dispatch = useAppDispatch();

  const devicesInRoom = useAppSelector(selectHttpDevicesInRoom);
  const singleRoom = useAppSelector(selectHttpSingleRoomFull);
  const selectedRooms = useAppSelector((s) => s.design.selectedRooms);
  const copySpaceMode = useAppSelector((s) => s.design.copySpaceModeActive);

  const isLocked = singleRoom?.multiplacerCeilingDevicesLocked?.bool === true;
  const productCount = devicesInRoom?.length ?? 0;

  const hideCopyButton = selectedRooms.length > 1 || productCount === 0 || copySpaceMode;

  const copyTooltip = copySpaceMode
    ? "Avslutt kopieringsmodus"
    : "Start kopieringsmodus for dette rommet";

  // Gemensam knapphöjd för en mer polerad look
  const buttonHeight = 30;

  const mlDevicesAvailable = useAppSelector(selectMlDevicesAvailableInSelectedSpace);

  const hasStandardRoom = singleRoom?.rfpMalRom != null && singleRoom?.rfpMalRom !== "";

  return (
    <Box
      bg="gray.0"
      p="xs"
      mb="xs"
      style={{
        borderRadius: "var(--mantine-radius-md)",
        border: "1px solid var(--mantine-color-gray-3)",
        boxShadow: "var(--mantine-shadow-xs)",
      }}
    >
      <Group justify="space-between" align="center">
        {/* Left side: Status and count */}

        {/* Right side: Actions */}
        <Group gap={6}>
          {/* Lock / unlock */}
          <RoomLockToggleButton />

          <DeviceTypePicker
            onSelect={(deviceType) => dispatch(addDeviceTypeToSelectedRoomThunk(deviceType))}
            disabled={isLocked}
          />
          {/* AI suggestions */}
          {mlDevicesAvailable && (
            <Tooltip label="Legg inn komponenter fra AI-forslag">
              <Button
                h={buttonHeight}
                size="xs"
                variant="light"
                color="blue"
                leftSection={<FaBrain size={14} />}
                disabled={isLocked}
                radius="md"
                onClick={() => dispatch(thunkAddMlDevicesToSelectedRoom())}
              >
                Bruk AI-forslag
              </Button>
            </Tooltip>
          )}

          {/* Copy layout */}
          {!hideCopyButton && (
            <Tooltip label={copyTooltip}>
              <Button
                h={buttonHeight}
                size="xs"
                variant={copySpaceMode ? "filled" : "outline"}
                color="blue"
                leftSection={<FaCopy size={14} />}
                disabled={isLocked}
                radius="md"
                onClick={() => dispatch(setCopySpaceModeActiveInRedux(!copySpaceMode))}
              >
                Kopier oppsett
              </Button>
            </Tooltip>
          )}

          {/* Copy from standard room */}
          {hasStandardRoom && (
            <Tooltip label="Kopier komponenter fra standardrom">
              <Button
                h={buttonHeight}
                size="xs"
                variant="outline"
                leftSection={<FaList size={14} />}
                disabled={isLocked}
                radius="md"
                onClick={() => dispatch(copyDevicesFromStandardRoomToSelectedRoomThunk())}
              >
                Standardrom
              </Button>
            </Tooltip>
          )}
          {devicesInRoom.length > 0 && <DeleteAllDevicesInRoomButton />}
        </Group>
      </Group>
    </Box>
  );
};
